import logger from '../../utils/logger';
// AdminNotes Component - Notes/Conversations management
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  MessageSquare, Plus, Edit, Trash2, Download, Upload, Printer, Calendar, BookOpen
} from 'lucide-react';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminNotes({ 
  notes: propNotes,
  kbSections: propKbSections,
  adminKey, 
  onRefresh,
  onOpenTransferToKB
}) {
  // Always call useAdmin hook
  const contextValues = useAdmin() || {};
  
  // Use props if provided, otherwise fallback to context
  const adminNotes = propNotes || contextValues.adminNotes || [];
  const kbSections = propKbSections || contextValues.kbSections || [];
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  });
  const fetchNotes = onRefresh || contextValues.fetchNotes || (() => {});
  const formatDate = contextValues.formatDate || ((d) => d ? new Date(d).toLocaleDateString('fr-CA') : 'N/A');
  const language = contextValues.language || 'fr';

  const [selectedNote, setSelectedNote] = useState(null);
  const [noteDialog, setNoteDialog] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Transfer to KB state
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferData, setTransferData] = useState({ section_id: '', title: '', tags: '' });

  const handleSaveNote = async () => {
    if (!editingNote?.title || !editingNote?.content) {
      toast.error(language === 'fr' ? 'Titre et contenu requis' : 'Title and content required');
      return;
    }
    setLoading(true);
    try {
      if (editingNote.note_id) {
        await api.put(`/admin/notes/${editingNote.note_id}`, editingNote);
      } else {
        await api.post('/admin/notes', editingNote);
      }
      toast.success(language === 'fr' ? 'Note sauvegardée!' : 'Note saved!');
      setNoteDialog(false);
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cette note?' : 'Delete this note?')) return;
    setLoading(true);
    try {
      await api.delete(`/admin/notes/${noteId}`);
      toast.success(language === 'fr' ? 'Note supprimée!' : 'Note deleted!');
      if (selectedNote?.note_id === noteId) setSelectedNote(null);
      fetchNotes();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintNote = (note) => {
    const html = `
      <html>
        <head>
          <title>${note.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #003DA5; border-bottom: 2px solid #003DA5; padding-bottom: 10px; }
            .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
            .content { white-space: pre-wrap; line-height: 1.6; }
            .category { background: #f0f0f0; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>${note.title}</h1>
          <div class="meta">
            <span class="category">${note.category}</span> | 
            ${language === 'fr' ? 'Créé' : 'Created'}: ${formatDate(note.created_at)}
          </div>
          <div class="content">${note.content}</div>
        </body>
      </html>
    `;
    // Render via Blob URL (avoids document.write / XSS with user content)
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  };

  const handleExportNotes = () => {
    const exportData = JSON.stringify(adminNotes, null, 2);
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payhub_notes_export_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(language === 'fr' ? 'Notes exportées!' : 'Notes exported!');
  };

  const handleImportNotes = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        const notesToImport = JSON.parse(text);
        
        const existingTitles = new Set(adminNotes.map(n => n.title.trim().toLowerCase()));
        let imported = 0;
        let skipped = 0;
        
        for (const note of notesToImport) {
          const titleLower = (note.title || '').trim().toLowerCase();
          if (existingTitles.has(titleLower)) {
            skipped++;
            continue;
          }
          
          try {
            await api.post('/admin/notes', {
              title: note.title,
              content: note.content,
              category: note.category || 'conversation',
              language: note.language || 'fr',
              is_pinned: note.is_pinned || false
            });
            imported++;
            existingTitles.add(titleLower);
          } catch (err) {
            logger.error('Error importing note:', note.title, err);
          }
        }
        
        const msg = language === 'fr' 
          ? `${imported} notes importées${skipped > 0 ? `, ${skipped} doublons ignorés` : ''}!`
          : `${imported} notes imported${skipped > 0 ? `, ${skipped} duplicates skipped` : ''}!`;
        toast.success(msg);
        fetchNotes();
      } catch (err) {
        toast.error(language === 'fr' ? 'Erreur d\'import' : 'Import error');
      }
    };
    input.click();
  };

  const handleRemoveDuplicates = async () => {
    if (!window.confirm(language === 'fr' 
      ? 'Supprimer les notes en double (garder la plus récente)?' 
      : 'Remove duplicate notes (keep the most recent)?')) {
      return;
    }
    
    const notesByTitle = {};
    for (const note of adminNotes) {
      const key = note.title.trim().toLowerCase();
      if (!notesByTitle[key]) {
        notesByTitle[key] = [];
      }
      notesByTitle[key].push(note);
    }
    
    let deleted = 0;
    for (const [title, notes] of Object.entries(notesByTitle)) {
      if (notes.length > 1) {
        notes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        for (let i = 1; i < notes.length; i++) {
          try {
            await api.delete(`/admin/notes/${notes[i].note_id}`);
            deleted++;
          } catch (err) {
            logger.error('Error deleting duplicate:', notes[i].title, err);
          }
        }
      }
    }
    
    if (deleted > 0) {
      toast.success(language === 'fr' 
        ? `${deleted} doublons supprimés!` 
        : `${deleted} duplicates removed!`);
      fetchNotes();
    } else {
      toast.info(language === 'fr' 
        ? 'Aucun doublon trouvé' 
        : 'No duplicates found');
    }
  };

  const openTransferToKBDialog = () => {
    if (selectedNote) {
      setTransferData({
        section_id: '',
        title: selectedNote.title,
        tags: ''
      });
      setTransferDialog(true);
    }
  };

  const handleTransferToKB = async () => {
    if (!transferData.section_id || !selectedNote) {
      toast.error(language === 'fr' ? 'Sélectionnez une section' : 'Select a section');
      return;
    }
    setLoading(true);
    try {
      await api.post('/admin/knowledge-base/articles', {
        title: transferData.title || selectedNote.title,
        section_id: transferData.section_id,
        content: selectedNote.content,
        tags: transferData.tags.split(',').map(t => t.trim()).filter(t => t),
        language: selectedNote.language || 'fr'
      });
      toast.success(language === 'fr' ? 'Note transférée vers KB!' : 'Note transferred to KB!');
      setTransferDialog(false);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de transfert' : 'Transfer error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        {/* Notes Header Actions */}
        <div className="flex flex-wrap justify-between gap-4">
          <div className="flex gap-2">
            <Button onClick={() => { setEditingNote({ title: '', content: '', category: 'conversation', language: language }); setNoteDialog(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Nouvelle Note' : 'New Note'}
            </Button>
            <Button variant="outline" onClick={handleExportNotes}>
              <Download className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Exporter' : 'Export'}
            </Button>
            <Button variant="outline" onClick={handleImportNotes}>
              <Upload className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Importer' : 'Import'}
            </Button>
            <Button 
              variant="outline"
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              onClick={handleRemoveDuplicates}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Suppr. doublons' : 'Remove duplicates'}
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            {adminNotes.length} note(s) {language === 'fr' ? 'enregistrée(s)' : 'saved'}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {/* Notes List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Notes ({adminNotes.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[500px] overflow-y-auto">
              <div className="space-y-2">
                {adminNotes.length > 0 ? adminNotes.map((note) => (
                  <div
                    key={note.note_id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                      selectedNote?.note_id === note.note_id 
                        ? 'bg-primary/10 border border-primary' 
                        : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => setSelectedNote(note)}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm line-clamp-1">{note.title}</p>
                      <span className="text-xs text-muted-foreground uppercase">{note.language}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{note.category}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(note.created_at).toLocaleDateString('fr-CA')}
                      </span>
                    </div>
                    <div className="hidden group-hover:flex gap-1 mt-2">
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={(e) => { e.stopPropagation(); setEditingNote(note); setNoteDialog(true); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={(e) => { e.stopPropagation(); handlePrintNote(note); }}>
                        <Printer className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.note_id); }}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground text-sm">
                      {language === 'fr' ? 'Aucune note' : 'No notes'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === 'fr' ? 'Créez une note pour sauvegarder vos conversations' : 'Create a note to save your conversations'}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Note Preview */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {selectedNote ? selectedNote.title : (language === 'fr' ? 'Aperçu Note' : 'Note Preview')}
                </CardTitle>
                {selectedNote && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={openTransferToKBDialog} title={language === 'fr' ? 'Transférer vers Base de connaissances' : 'Transfer to Knowledge Base'}>
                      <BookOpen className="h-4 w-4 mr-1" />
                      {language === 'fr' ? 'Vers KB' : 'To KB'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handlePrintNote(selectedNote)}>
                      <Printer className="h-4 w-4 mr-1" />PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setEditingNote(selectedNote); setNoteDialog(true); }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDeleteNote(selectedNote.note_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="max-h-[450px] overflow-y-auto">
              {selectedNote ? (
                <div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">{selectedNote.category}</span>
                    <span className="text-xs px-2 py-1 rounded-full bg-muted">{selectedNote.language === 'fr' ? 'Français' : 'English'}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-lg border-l-4 border-primary">
                    {selectedNote.content}
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    {language === 'fr' ? 'Créé' : 'Created'}: {formatDate(selectedNote.created_at)} | 
                    {language === 'fr' ? ' Mis à jour' : ' Updated'}: {formatDate(selectedNote.updated_at)}
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{language === 'fr' ? 'Sélectionnez une note' : 'Select a note'}</p>
                    <p className="text-xs mt-1">{language === 'fr' ? 'ou créez-en une nouvelle' : 'or create a new one'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Note Edit Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingNote?.note_id 
                ? (language === 'fr' ? 'Modifier Note' : 'Edit Note')
                : (language === 'fr' ? 'Nouvelle Note' : 'New Note')
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
              <Input 
                value={editingNote?.title || ''} 
                onChange={(e) => setEditingNote({...editingNote, title: e.target.value})}
              />
            </div>
            <div>
              <Label>{language === 'fr' ? 'Contenu' : 'Content'}</Label>
              <Textarea 
                rows={10}
                value={editingNote?.content || ''} 
                onChange={(e) => setEditingNote({...editingNote, content: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{language === 'fr' ? 'Catégorie' : 'Category'}</Label>
                <Select 
                  value={editingNote?.category || 'conversation'}
                  onValueChange={(value) => setEditingNote({...editingNote, category: value})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conversation">Conversation</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="feature">{language === 'fr' ? 'Fonctionnalité' : 'Feature'}</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="other">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{language === 'fr' ? 'Langue' : 'Language'}</Label>
                <Select 
                  value={editingNote?.language || 'fr'}
                  onValueChange={(value) => setEditingNote({...editingNote, language: value})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleSaveNote} disabled={loading}>
              {language === 'fr' ? 'Sauvegarder' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer to KB Dialog */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {language === 'fr' ? 'Transférer vers Base de connaissances' : 'Transfer to Knowledge Base'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Section</Label>
              <Select 
                value={transferData.section_id}
                onValueChange={(value) => setTransferData({...transferData, section_id: value})}
              >
                <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionner une section' : 'Select a section'} /></SelectTrigger>
                <SelectContent>
                  {kbSections.map((s) => (
                    <SelectItem key={s.section_id} value={s.section_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{language === 'fr' ? 'Titre de l\'article' : 'Article Title'}</Label>
              <Input 
                value={transferData.title} 
                onChange={(e) => setTransferData({...transferData, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Tags ({language === 'fr' ? 'séparés par virgule' : 'comma separated'})</Label>
              <Input 
                value={transferData.tags} 
                onChange={(e) => setTransferData({...transferData, tags: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleTransferToKB} disabled={loading || !transferData.section_id}>
              {language === 'fr' ? 'Transférer' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
